import { fetchPmsSyncStatus } from 'features/Welcome/HotelSwitcher/pmsSyncStatusSlice'
import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { selectIsInternalAdmin } from '../../../accessors/index'
import { HotelSelectorGroup } from './HotelSelectorGroup'
import AssignedIcon from './my-assigned.svg'

export const HotelSelector = ({ children, handleSelect }) => {
  const [searchParam, setSearchParam] = useState('')

  const inputRef = useRef()

  const dispatch = useDispatch()

  useEffect(() => {
    if (inputRef.current) {
      setTimeout(() => {
        inputRef.current.focus()
      }, 0)
    }
  }, [])

  const handleFilterChange = e => setSearchParam(e.target.value)

  const subsidiaryList = useSelector(s => s.login.subsidiaryList)

  const currentSubsidiaryId = useSelector(s => s.login.currentHotelId)

  const isInternalAdmin = useSelector(selectIsInternalAdmin)

  const isCsmUser = useSelector(s => s.login.isCsmUser)

  useEffect(() => {
    if (isInternalAdmin) {
      dispatch(fetchPmsSyncStatus())
    }
  }, [dispatch, isInternalAdmin])

  const myAssignedSubsidiaries = subsidiaryList.filter(s =>
    isCsmUser ? s.isCurrentUserCsm : true
  )
  const otherSubsidiaries = isCsmUser
    ? subsidiaryList.filter(s => !s.isCurrentUserCsm)
    : subsidiaryList

  const groupedMyAssignedSubsidiaries = myAssignedSubsidiaries.reduce((prev, curr) => {
    if (prev[curr.merchantName]) {
      return {
        ...prev,
        [curr.merchantName]: [...prev[curr.merchantName], curr]
      }
    } else {
      return { ...prev, [curr.merchantName]: [curr] }
    }
  }, {})

  const formattedMyAssignedSubsidiaries = Object.keys(
    groupedMyAssignedSubsidiaries
  ).reduce((prev, curr) => {
    return { ...prev, [curr]: groupedMyAssignedSubsidiaries[curr] }
  }, {})

  const groupedOtherSubsidiaries = otherSubsidiaries.reduce((prev, curr) => {
    if (prev[curr.merchantName]) {
      return {
        ...prev,
        [curr.merchantName]: [...prev[curr.merchantName], curr]
      }
    } else {
      return { ...prev, [curr.merchantName]: [curr] }
    }
  }, {})

  const formattedOtherSubsidiaries = Object.keys(groupedOtherSubsidiaries).reduce(
    (prev, curr) => {
      return { ...prev, [curr]: groupedOtherSubsidiaries[curr] }
    },
    {}
  )

  if (!subsidiaryList) {
    return 'loading'
  }

  function switchToPreviousHotel() {
    const flatSubsidiaries = Object.values(formattedOtherSubsidiaries).flat()

    const currentHotelIndex = flatSubsidiaries.findIndex(
      s => s.subsidiaryId === currentSubsidiaryId
    )

    const previousHotelIndex = currentHotelIndex - 1

    if (previousHotelIndex < 0) {
      return
    }

    const previousHotel = flatSubsidiaries[previousHotelIndex]

    handleSelect(previousHotel)
  }

  function switchToNextHotel() {
    const flatSubsidiaries = Object.values(formattedOtherSubsidiaries).flat()

    const currentHotelIndex = flatSubsidiaries.findIndex(
      s => s.subsidiaryId === currentSubsidiaryId
    )

    const nextHotelIndex = currentHotelIndex + 1

    if (nextHotelIndex >= flatSubsidiaries.length) {
      return
    }

    const nextHotel = flatSubsidiaries[nextHotelIndex]

    handleSelect(nextHotel)
  }

  return (
    <div className="hotel-selector">
      <div className="hotel-selector__filter" style={{ display: 'flex' }}>
        <input
          type="search"
          placeholder="Search..."
          onChange={handleFilterChange}
          value={searchParam}
          ref={inputRef}
        />
        {currentSubsidiaryId && (
          <>
            <button
              className="hotel-selector__prev-button"
              onClick={switchToPreviousHotel}
            >
              <span className="hotel-selector__prev-button-arrow">←</span>
            </button>
            <button className="hotel-selector__next-button" onClick={switchToNextHotel}>
              <span className="hotel-selector__next-button-arrow">→</span>
            </button>
          </>
        )}
      </div>
      <ul>
        {isCsmUser && (
          <>
            <div className="hotel-selector__my-assigned">
              <img src={AssignedIcon} alt="Assigned" />
              Hotels you’re assigned to
            </div>
            <HotelSelectorGroup
              groupName="Independently Managed Hotels"
              isInternalAdmin={isInternalAdmin}
              formattedSubsidiaries={formattedMyAssignedSubsidiaries}
              searchParam={searchParam}
              handleSelect={handleSelect}
              key="Independently Managed Hotels"
            />
            {Object.keys(formattedMyAssignedSubsidiaries)
              .filter(k => k !== 'Independently Managed Hotels')
              .sort((a, b) => a.localeCompare(b))
              .map(groupName => (
                <HotelSelectorGroup
                  groupName={groupName}
                  isInternalAdmin={isInternalAdmin}
                  formattedSubsidiaries={formattedMyAssignedSubsidiaries}
                  searchParam={searchParam}
                  handleSelect={handleSelect}
                  key={groupName}
                />
              ))}
            <div className="hotel-selector__others">Other Hotels</div>
          </>
        )}
        <HotelSelectorGroup
          groupName="Independently Managed Hotels"
          isInternalAdmin={isInternalAdmin}
          formattedSubsidiaries={formattedOtherSubsidiaries}
          searchParam={searchParam}
          handleSelect={handleSelect}
          key="Independently Managed Hotels"
        />
        {Object.keys(formattedOtherSubsidiaries)
          .filter(k => k !== 'Independently Managed Hotels')
          .sort((a, b) => a.localeCompare(b))
          .map(groupName => (
            <HotelSelectorGroup
              groupName={groupName}
              isInternalAdmin={isInternalAdmin}
              formattedSubsidiaries={formattedOtherSubsidiaries}
              searchParam={searchParam}
              handleSelect={handleSelect}
              key={groupName}
            />
          ))}
        {children}
      </ul>
    </div>
  )
}
